import { numericToNumber } from "@/utils/numericToNumber";
import { useAuth } from "@/contexts/AuthContext";
import { CardWithRelations } from "@/hooks/useCards";
import { CardVariantItemWatchlist } from "./CardVariantItemWatchlist";
import { CardVariantWithBinder } from "@/types/varinats";
import { CardVariantItemBinders } from "./CardVariantItemBinders";

export interface Props {
  card: CardWithRelations;
  variant: CardVariantWithBinder;
  scanning?: boolean;
}

export function CardVariantItem({ card, variant, scanning = false }: Props) {
  const { user } = useAuth();

  const conditionLabel = variant.condition.name || "Unknown Condition";
  const printingLabel = variant.printing.name || "Unknown Printing";

  const priceValue = variant.price ? numericToNumber(variant.price) : 0;
  const priceText = `$${priceValue.toFixed(2)}`;

  const hasPriceChange24h =
    variant.priceChange24Hr !== undefined && variant.priceChange24Hr !== null;
  const priceChange24hValue = hasPriceChange24h
    ? numericToNumber(variant.priceChange24Hr)
    : null;
  const priceChange24hClass =
    priceChange24hValue !== null
      ? priceChange24hValue >= 0
        ? "text-green-600"
        : "text-red-600"
      : "";
  const priceChange24hPrefix =
    priceChange24hValue !== null && priceChange24hValue > 0 ? "+" : "";
  const priceChange24hText =
    priceChange24hValue !== null
      ? `${priceChange24hPrefix}${priceChange24hValue.toFixed(2)}% (24h)`
      : null;

  const hasAvgPrice =
    variant.avgPrice !== undefined && variant.avgPrice !== null;
  const avgPriceText = hasAvgPrice
    ? `$${numericToNumber(variant.avgPrice).toFixed(2)}`
    : null;

  const hasMin7d =
    variant.minPrice7D !== undefined && variant.minPrice7D !== null;
  const min7dText = hasMin7d
    ? `$${numericToNumber(variant.minPrice7D).toFixed(2)}`
    : null;

  const hasMax7d =
    variant.maxPrice7D !== undefined && variant.maxPrice7D !== null;
  const max7dText = hasMax7d
    ? `$${numericToNumber(variant.maxPrice7D).toFixed(2)}`
    : null;

  const hasChange7d =
    variant.priceChange7D !== undefined && variant.priceChange7D !== null;
  const change7dValue = hasChange7d
    ? numericToNumber(variant.priceChange7D)
    : null;
  const change7dClass =
    change7dValue !== null
      ? change7dValue >= 0
        ? "text-green-600"
        : "text-red-600"
      : "";
  const change7dPrefix = change7dValue !== null && change7dValue > 0 ? "+" : "";
  const change7dText =
    change7dValue !== null
      ? `${change7dPrefix}${change7dValue.toFixed(2)}%`
      : null;

  return (
    <div className="grid border rounded-lg  transition-colors gap-4">
      <div className="grid gap-2 p-4">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <span className="font-medium text-lg">{conditionLabel}</span>
            <span className="text-sm text-muted-foreground">
              {printingLabel}
            </span>
          </div>
          <div className="text-right">
            <div className="font-bold text-xl">{priceText}</div>
            {priceChange24hText !== null && (
              <div className={`text-sm ${priceChange24hClass}`}>
                {priceChange24hText}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          {hasAvgPrice && (
            <div>
              <span className="text-muted-foreground">Avg: </span>
              <span className="font-medium">{avgPriceText}</span>
            </div>
          )}
          {hasMin7d && (
            <div className="text-center">
              <span className="text-muted-foreground">7d Low: </span>
              <span className="font-medium">{min7dText}</span>
            </div>
          )}
          {hasMax7d && (
            <div className="text-center">
              <span className="text-muted-foreground">7d High: </span>
              <span className="font-medium">{max7dText}</span>
            </div>
          )}
          {hasChange7d && (
            <div className="text-right">
              <span className="text-muted-foreground">7d Change: </span>
              <span className={`font-medium ${change7dClass}`}>
                {change7dText}
              </span>
            </div>
          )}
        </div>
        {user && (
          <div className="flex flex-wrap gap-2">
            <CardVariantItemWatchlist variant={variant} />
            <CardVariantItemBinders variant={variant} card={card} />
          </div>
        )}
      </div>
    </div>
  );
}
